/**
 * useInAppBrowserGuard
 *
 * Detects whether the page is running inside an in-app browser (Instagram,
 * Facebook, LinkedIn, Gmail, WeChat, Android WebView, iOS WKWebView without
 * Safari, etc.) and provides helpers to escape to the system default browser.
 *
 * Usage:
 *   const { isInAppBrowser, isIOS, isAndroid, escapeUrl } = useInAppBrowserGuard()
 */

/** Well-known in-app browser UA patterns */
const IN_APP_UA_RE =
  /FBAN|FBIOS|FB_IAB|Instagram|LinkedInApp|Twitter(?:Android|ForIPhone)|\bGSA\/|\bSnapchat\/|MicroMessenger|Line\/\d|Pinterest|TikTok|musical_ly/i;

/**
 * Returns true when the running context is an iOS WKWebView:
 * - Has AppleWebKit
 * - Is a mobile device (iPhone / iPad / iPod)
 * - Does NOT include "Safari" in the UA (Safari always includes its own name)
 * - Does NOT include "CriOS" (Chrome for iOS) or "FxiOS" (Firefox for iOS)
 *   because those are legitimate browsers on iOS, just not Safari.
 */
function detectIOSWebView(ua: string): boolean {
  return (
    /AppleWebKit/i.test(ua) &&
    /iPhone|iPad|iPod/i.test(ua) &&
    !/Safari/i.test(ua) &&
    !/CriOS/i.test(ua) &&
    !/FxiOS/i.test(ua)
  );
}

/**
 * Returns true when the running context is an Android WebView:
 * - Chrome UA on Android that includes "; wv)" marker
 * - OR explicit "Android" + "WebView" combo (older Android system WebView)
 */
function detectAndroidWebView(ua: string): boolean {
  return /Android/.test(ua) && (/; wv\)/.test(ua) || /\bWebView\b/i.test(ua));
}

/**
 * Build an Android intent:// URL that asks the system to open the given URL
 * in the default browser. Falls back to the original URL if construction
 * fails (e.g., URL parse error in older environments).
 */
function buildIntentUrl(rawUrl: string): string {
  try {
    const url = new URL(rawUrl);
    // intent://<host><path><search>#Intent;scheme=https;action=android.intent.action.VIEW;end
    // Deliberately EXCLUDE url.hash: the fragment is the only attacker-influenced,
    // client-only part of a deep-link and serves no purpose here (the SMS landing
    // routes read route params, never the fragment). Interpolating it raw would
    // splice attacker-controlled `#Intent;...` markers into the intent URI — inert
    // today (Android Intent.parseUri anchors on the LAST `#`, so our trailing block
    // wins) but pointless to carry (cas-1c9a persona-review hardening).
    const path = url.pathname + url.search;
    return `intent://${url.host}${path}#Intent;scheme=${url.protocol.replace(':', '')};action=android.intent.action.VIEW;end`;
  } catch {
    return rawUrl;
  }
}

export function useInAppBrowserGuard() {
  const isInAppBrowser = ref(false);
  const isIOS = ref(false);
  const isAndroid = ref(false);

  /**
   * The URL the "Open in browser" button should navigate to.
   * On Android: intent:// URL that triggers the system browser chooser.
   * On iOS: plain current URL (displayed for copy-paste; no deep-link scheme
   *   can reliably force Safari from inside an in-app browser).
   */
  const escapeUrl = ref('');

  onMounted(() => {
    const ua = navigator.userAgent;
    const currentUrl = window.location.href;

    const iosDevice = /iPhone|iPad|iPod/i.test(ua);
    const androidDevice = /Android/i.test(ua);

    isIOS.value = iosDevice;
    isAndroid.value = androidDevice;

    const knownInApp = IN_APP_UA_RE.test(ua);
    const iosWebview = iosDevice && detectIOSWebView(ua);
    const androidWebview = androidDevice && detectAndroidWebView(ua);

    isInAppBrowser.value = knownInApp || iosWebview || androidWebview;

    if (isInAppBrowser.value) {
      escapeUrl.value = androidDevice ? buildIntentUrl(currentUrl) : currentUrl;
    }
  });

  return {
    isInAppBrowser,
    isIOS,
    isAndroid,
    escapeUrl
  };
}
